import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Ship, Anchor, Ruler, Gauge, Fuel, Users2, MapPin, Search, Waves, LifeBuoy, Star, SlidersHorizontal } from "lucide-react";
import { crm } from "@/lib/crm";
import SmartImg from "@/components/SmartImg";
import { cleanTitle } from "@/lib/format";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";

const FAM_ICON = { motor_yacht: Ship, sailboat: Ship, motorboat: Ship, catamaran: Waves, rib: LifeBuoy, jetski: Waves, houseboat: Anchor, fishing: Anchor, other: Anchor };
const euro = (n, c = "EUR") => (n == null ? "Sur demande" : `${Number(n).toLocaleString("fr-FR")} ${c === "EUR" ? "€" : c}`);

const SORTS = [["recent", "Plus récents"], ["price_asc", "Prix croissant"], ["price_desc", "Prix décroissant"], ["length_desc", "Plus grands"]];

export default function Boats() {
  const [facets, setFacets] = useState(null);
  const [family, setFamily] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [fuel, setFuel] = useState("");
  const [lengthMin, setLengthMin] = useState("");
  const [powerMin, setPowerMin] = useState("");
  const [yearMin, setYearMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { crm("/boats/facets").then(setFacets).catch(() => setFacets({ total: 0, families: [] })); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: 48, sort });
    if (family) p.set("family", family);
    if (q) p.set("q", q);
    if (fuel) p.set("fuel", fuel);
    if (lengthMin) p.set("length_min", lengthMin);
    if (powerMin) p.set("power_min", powerMin);
    if (yearMin) p.set("year_min", yearMin);
    if (priceMax) p.set("price_max", priceMax);
    crm(`/boats?${p}`, { noCache: true }).then(setData).catch(() => setData({ total: 0, rows: [] })).finally(() => setLoading(false));
  }, [family, q, sort, fuel, lengthMin, powerMin, yearMin, priceMax]);
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const resetFilters = () => { setFuel(""); setLengthMin(""); setPowerMin(""); setYearMin(""); setPriceMax(""); };
  const activeFilters = [fuel, lengthMin, powerMin, yearMin, priceMax].filter(Boolean).length;

  const rows = data?.rows || [];
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Seo title="Bateaux à vendre — Yachts, Voiliers & Bateaux à moteur | Pactly" description="Yachts, voiliers, bateaux à moteur, catamarans et semi-rigides sélectionnés auprès de professionnels vérifiés." />

      <header className="mb-8">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-emerald-400"><Anchor size={16} /> Pactly Marine</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Bateaux à vendre</h1>
        <p className="mt-3 max-w-2xl text-base text-white/60">Yachts, voiliers, bateaux à moteur et plus — sélectionnés auprès de professionnels vérifiés à travers l'Europe.</p>
      </header>

      {/* Familles */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="boat-family-filters">
        <FamilyChip active={family === ""} onClick={() => setFamily("")} icon={Ship} label="Tous" count={facets?.total} />
        {(facets?.families || []).map((f) => (
          <FamilyChip key={f.key} active={family === f.key} onClick={() => setFamily(f.key)}
            icon={FAM_ICON[f.key] || Anchor} label={f.label} count={f.count} testid={`family-chip-${f.key}`} />
        ))}
      </div>

      {/* Barre outils */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input data-testid="boats-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marque, modèle, type…" className="border-white/10 bg-white/5 pl-9 text-white" />
        </div>
        <select data-testid="boats-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-lg border border-white/10 bg-[#0F2540] px-3 py-2.5 text-sm text-white">
          {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button data-testid="boats-filters-toggle" onClick={() => setShowFilters((v) => !v)}
          className={`flex items-center gap-1.5 rounded-lg border px-3 py-2.5 text-sm font-semibold transition ${showFilters || activeFilters ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-200" : "border-white/10 bg-white/5 text-white/60 hover:text-white"}`}>
          <SlidersHorizontal size={15} /> Filtres{activeFilters ? ` (${activeFilters})` : ""}
        </button>
        <span className="text-sm text-white/50">{(data?.total || 0).toLocaleString("fr-FR")} bateau{(data?.total || 0) > 1 ? "x" : ""}</span>
      </div>

      {showFilters && (
        <div className="mb-6 grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="boats-advanced-filters">
          <label className="text-xs font-semibold text-white/60">Longueur min (m)
            <input data-testid="filter-length" type="number" min="0" value={lengthMin} onChange={(e) => setLengthMin(e.target.value)} placeholder={facets?.ranges?.length_max ? `≤ ${facets.ranges.length_max}` : ""} className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400/50" />
          </label>
          <label className="text-xs font-semibold text-white/60">Puissance min (hp)
            <input data-testid="filter-power" type="number" min="0" value={powerMin} onChange={(e) => setPowerMin(e.target.value)} placeholder={facets?.ranges?.power_max ? `≤ ${facets.ranges.power_max}` : ""} className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400/50" />
          </label>
          <label className="text-xs font-semibold text-white/60">Année min
            <input data-testid="filter-year" type="number" min="1900" value={yearMin} onChange={(e) => setYearMin(e.target.value)} placeholder={facets?.ranges?.year_min ? `${facets.ranges.year_min}+` : ""} className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400/50" />
          </label>
          <label className="text-xs font-semibold text-white/60">Prix max (€)
            <input data-testid="filter-price" type="number" min="0" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder={facets?.ranges?.price_max ? `≤ ${facets.ranges.price_max.toLocaleString("fr-FR")}` : ""} className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400/50" />
          </label>
          <label className="text-xs font-semibold text-white/60">Carburant
            <select data-testid="filter-fuel" value={fuel} onChange={(e) => setFuel(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-[#0F2540] px-3 py-2 text-sm text-white">
              <option value="">Tous</option>
              {(facets?.fuels || []).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          {activeFilters > 0 && (
            <button data-testid="filter-reset" onClick={resetFilters} className="col-span-2 self-end text-left text-sm font-semibold text-rose-300 hover:underline sm:col-span-1">Réinitialiser les filtres</button>
          )}
        </div>
      )}

      {loading && !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-white/5" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-white/10 bg-white/5 py-24 text-center text-white/50">
          <Ship size={40} className="opacity-40" /><p className="mt-3 text-sm">Aucun bateau pour ces critères.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="boats-grid">
          {rows.map((b) => <BoatTile key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
}

function FamilyChip({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`group flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${active ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-200" : "border-white/10 bg-white/5 text-white/60 hover:border-white/25 hover:text-white"}`}>
      <Icon size={15} /> {label}
      {count != null && <span className={`rounded-full px-1.5 text-[11px] ${active ? "bg-emerald-400/25 text-emerald-100" : "bg-white/10 text-white/50"}`}>{count}</span>}
    </button>
  );
}

export function BoatTile({ b }) {
  return (
    <Link to={`/bateau/${b.id}`} data-testid={`boat-tile-${b.id}`}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-1 hover:border-emerald-400/30 hover:shadow-xl hover:shadow-emerald-500/5">
      <div className="relative aspect-[4/3] overflow-hidden bg-[#0B1E38]">
        <SmartImg src={b.images?.[0] || ""} alt={b.title} vtype="boat" className="h-full w-full"
          imgClassName="transition duration-500 group-hover:scale-105" />
        <div className="absolute left-2.5 top-2.5 flex items-center gap-1.5">
          <span className="rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur">{b.boat_family_label}</span>
          {b.featured && <span className="flex items-center gap-1 rounded-full bg-amber-400/90 px-2 py-1 text-[11px] font-bold text-[#0B1E38]"><Star size={10} className="fill-[#0B1E38]" /> Coup de cœur</span>}
        </div>
        {b.boat_type && <span className="absolute bottom-2.5 left-2.5 rounded bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white/90 backdrop-blur">{b.boat_type}</span>}
      </div>
      <div className="p-4">
        <div className="truncate font-display text-[15px] font-bold text-white">{cleanTitle(b.make, b.model)}</div>
        <div className="mt-1 font-display text-lg font-extrabold text-emerald-300">{euro(b.price, b.currency)}</div>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-white/55">
          {b.year && <span>{b.year}</span>}
          {b.length_m && <span className="flex items-center gap-1"><Ruler size={12} /> {b.length_m} m</span>}
          {b.total_power_hp && <span className="flex items-center gap-1"><Gauge size={12} /> {b.total_power_hp} hp</span>}
          {b.cabins != null && <span className="flex items-center gap-1"><Users2 size={12} /> {b.cabins}</span>}
        </div>
        {b.location && <div className="mt-2 flex items-center gap-1 text-[12px] text-white/40"><MapPin size={12} /> {b.location}</div>}
      </div>
    </Link>
  );
}
